import { getAhaMomentImageSource } from '@fruitful/conversational-mm';
import { formatDollars } from '@fruitful/fruitils';
import { buildAhaResult } from '@fruitful/money-map-simulator';
import { AnalyticsUserEvent, MoneyMapStatus } from '@fruitful/types';
import { useCallback, useRef, useState } from 'react';
import { type View as RNView } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    ExpandingTilePreview,
    FontFamilyName,
    Image,
    Maximize2,
    Text,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { SIDEBAR_CONTENT_MAX_WIDTH } from '../../../common/utils/homeUtils';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { useGetMembershipMastraThread } from '../../../llm-service/hooks/useGetMembershipMastraThread';
import { track } from '../../../logger/Logger';
import { useGetMoneyMapForMembership } from '../../../roadmap/hooks/useGetMoneyMapsForMembership';
import { ExpandingGoalTileOverlay } from './ExpandingGoalTileOverlay';
import {
    MoneyMapProjectionGraphic,
    useMoneyMapProjectionDisplay,
} from './MoneyMapProjectionGraphic';

interface TileLayout {
    x: number;
    y: number;
    width: number;
    height: number;
}

const INVEST_READY_COPY = 'Your optimized investment system is ready!';

export const HomeGoalTile = () => {
    const { trackViewOnce } = useViewEvents();
    const user = useAuthedUser();
    const membershipId = user.membership?.id;
    const tileRef = useRef<RNView>(null);
    const [expanded, setExpanded] = useState(false);
    const [tileLayout, setTileLayout] = useState<TileLayout | null>(null);

    const { data: results } = useGetMembershipMastraThread({ membershipId });
    const { data: moneyMap, isLoading: isMoneyMapLoading } =
        useGetMoneyMapForMembership();

    const hasPublishedMoneyMap =
        moneyMap?.status === MoneyMapStatus.PREVIEW ||
        moneyMap?.status === MoneyMapStatus.ACTIVE;

    const projectionDisplay = useMoneyMapProjectionDisplay(results);

    const handlePress = useCallback(() => {
        track(AnalyticsUserEvent.CMM_HOME_GOAL_TILE_PRESSED);

        const node = tileRef.current;
        if (node == null) {
            setTileLayout({ x: 0, y: 0, width: 0, height: 0 });
            setExpanded(true);
            return;
        }

        node.measureInWindow((x, y, width, height) => {
            setTileLayout({ x, y, width, height });
            setExpanded(true);
        });
    }, []);

    const handleClose = useCallback(() => {
        setExpanded(false);
        setTileLayout(null);
    }, []);

    if (
        isMoneyMapLoading ||
        hasPublishedMoneyMap ||
        membershipId == null ||
        results == null
    ) {
        return null;
    }

    const ahaResult = buildAhaResult(results);
    const isInvesting = ahaResult.type === 'investing';

    if (projectionDisplay != null) {
        trackViewOnce('Home/CMM-GoalTile');

        return (
            <>
                <YStack
                    ref={tileRef}
                    width="100%"
                    $sm={{
                        maxWidth: SIDEBAR_CONTENT_MAX_WIDTH,
                        alignSelf: 'center',
                    }}
                >
                    <ExpandingTilePreview
                        eyebrow="Your goal"
                        icon={Maximize2}
                        fade={false}
                        noPaddingBottom={projectionDisplay.showSystemPreview}
                        graphic={
                            <MoneyMapProjectionGraphic
                                display={projectionDisplay}
                            />
                        }
                        title=""
                        previewContent=""
                        onPress={handlePress}
                    />
                </YStack>
                {expanded && tileLayout != null && (
                    <ExpandingGoalTileOverlay
                        tileLayout={tileLayout}
                        ahaResult={ahaResult}
                        results={results}
                        onClose={handleClose}
                        isVersion2
                        v2DisplayData={{
                            headline: projectionDisplay.headline,
                            dateSuffix: projectionDisplay.dateSuffix,
                            mode: projectionDisplay.mode,
                            showSystemPreview:
                                projectionDisplay.showSystemPreview,
                            adjusted: projectionDisplay.adjusted,
                            ruleSetDisplays: projectionDisplay.ruleSetDisplays,
                        }}
                    />
                )}
            </>
        );
    }

    const { displayHeadline, image } = ahaResult;
    const debtFreeDate = !isInvesting ? ahaResult.debtFreeDate : '';
    const imageSource = getAhaMomentImageSource(image);

    trackViewOnce('Home/CMM-GoalTile');

    const title = isInvesting ? (
        <Text
            fontSize={36}
            lineHeight={40}
            letterSpacing={-1.5}
            textAlign="center"
            color="$black"
            style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
        >
            {formatDollars(ahaResult.projectedTotalCents / 100, false)}
            {'\n'}invested by{'\n'}
            {ahaResult.projectedDate}
        </Text>
    ) : (
        <XStack flexWrap="wrap" gap={4} justifyContent="center">
            <BodyLargeRegular color="$black">
                {displayHeadline}
            </BodyLargeRegular>
            {debtFreeDate !== '' && (
                <BodyLargeEmphasized color="$black">
                    {debtFreeDate}
                </BodyLargeEmphasized>
            )}
        </XStack>
    );

    const previewContent = (
        <BodyMediumRegular
            color="$black"
            textAlign="center"
            lineHeight={24}
            paddingHorizontal={24}
        >
            {isInvesting
                ? INVEST_READY_COPY
                : debtFreeDate !== ''
                  ? `Based on everything you've told us, ${displayHeadline.toLowerCase()} ${debtFreeDate}.`
                  : 'Your Money Map is ready! Tap to see the full breakdown.'}
        </BodyMediumRegular>
    );

    return (
        <>
            <YStack
                ref={tileRef}
                width="100%"
                $sm={{
                    maxWidth: SIDEBAR_CONTENT_MAX_WIDTH,
                    alignSelf: 'center',
                }}
            >
                <ExpandingTilePreview
                    eyebrow="Your goal"
                    icon={Maximize2}
                    fade={false}
                    graphic={
                        <Image
                            source={imageSource}
                            style={{ width: '100%', height: 180 }}
                            resizeMode="contain"
                        />
                    }
                    title={title}
                    previewContent={previewContent}
                    onPress={handlePress}
                />
            </YStack>
            {expanded && tileLayout != null && (
                <ExpandingGoalTileOverlay
                    tileLayout={tileLayout}
                    ahaResult={ahaResult}
                    results={results}
                    onClose={handleClose}
                />
            )}
        </>
    );
};

export default HomeGoalTile;
